import { hsvaToHexa, rgbStringToHsva } from '@meitu/saas-colors';
import { type ILayerProps, type ITextContent, type ITextLayerProps } from '@/version-types';
import { cleanAndUnescapeHtml } from './base';

type TextContentStyleProps = Pick<
    ITextContent,
    'color' | 'fontSize' | 'fontFamily' | 'bold' | 'italic' | 'underline' | 'lineThrough'
>;

/**
 * @internal
 * 根据 DOM 节点的样式提取部分属性
 * @param el - DOM 节点
 * @returns 提取的部分属性
 */
const extractPartialPropsFromStyle = (el: HTMLElement) => {
    const partial = {} as TextContentStyleProps;

    const style = el.style || {};
    const targetStyleKeys = ['color', 'fontSize', 'fontWeight', 'fontStyle', 'textDecorationLine'];

    targetStyleKeys.forEach((key) => {
        switch (key) {
            case 'color': {
                const color = el.getAttribute('data-color') || hsvaToHexa(rgbStringToHsva(style[key]));
                partial.color = color;
                break;
            }
            case 'fontSize':
                partial.fontSize = +style[key].replace('px', '');
                break;
            case 'fontWeight':
                partial.bold = style[key] === 'bold';
                break;
            case 'fontStyle':
                partial.italic = style[key] === 'italic';
                break;
            case 'textDecorationLine':
                partial.underline = style[key] === 'underline';
                partial.lineThrough = style[key] === 'line-through';
                break;
            default:
                break;
        }
    });

    return partial;
};

/**
 * @internal
 * 将 DOM 节点转换为 TextContent
 * @param el - DOM 节点
 * @param fallbackFontFamily - 当无法从 DOM 节点中提取字体时的后备字体，必须传入
 * @returns TextContent
 */
const extractTextContentFromElement = (el: HTMLElement, fallbackFontFamily: string) => {
    if (!el) return null;

    const propsFromStyle = extractPartialPropsFromStyle(el);
    const textMark = JSON.parse(el.dataset?.mark ?? 'null');
    const text = cleanAndUnescapeHtml(el.innerHTML, {
        trimEndNewline: false,
    });

    const fontFamily = el?.dataset?.font || fallbackFontFamily;

    const textContent: ITextContent = {
        text,
        ...propsFromStyle,
        fontFamily,
        textMark,
    };

    return textContent;
};

/**
 * 从 DOM 节点（文本内容容器）提取多段文本内容
 * @param container - 包含文本内容的容器节点
 * @param fallbackFontFamily - 当无法从 DOM 节点中提取字体时的后备字体，必须传入
 * @returns 文本内容数组
 */
export const extractTextContentsFromElement = (container: HTMLElement, fallbackFontFamily: string): ITextContent[] => {
    const textContents: ITextContent[] = [];

    if (!container) return textContents;

    const childNodes = container.childNodes;
    if (childNodes.length === 0) return textContents;

    for (let i = 0; i < childNodes.length; i++) {
        const item = childNodes[i];
        const el = item as HTMLElement;
        const isCustom = el?.dataset?.custom === '1'; // 使用 '?' 操作符，防止低版本浏览器引擎报错

        if (!isCustom) continue;

        const textContent = extractTextContentFromElement(el, fallbackFontFamily);
        if (textContent) textContents.push(textContent);
    }

    return textContents;
};

/**
 * 标准化分段编辑数据
 * 若 `textContents` 为空，则根据通用文本属性生成一段
 * @param layerData - 文本图层数据
 * @returns
 */
export const normalizeTextContents = (layerData: ITextLayerProps) => {
    const { textContents = [] } = layerData;

    if (textContents.length > 0) return textContents;

    const textContent: ITextContent = {
        color: layerData.color,
        fontSize: layerData.fontSize,
        fontFamily: layerData.fontFamily,
        bold: layerData.bold,
        italic: layerData.italic,
        lineThrough: layerData.lineThrough,
        underline: layerData.underline,
        text: layerData.text,
        textMark: layerData.textMark,
    };

    return [textContent];
};

/**
 * 获取文本（段）内容的样式
 *
 * @param content - 文本内容
 * @param layer - 由于最终字体样式需要从 layer 的 runtime 中获取, 所以这里需要传入 layer
 * @param options - 可选；配置选项
 */
export const getTextContentsStyle = (
    content: ITextContent,
    layer: ILayerProps,
    options = { useTransparentFontColor: false },
) => {
    const fontFamilyMap = layer.__runtime?.fontFamilyMap;
    const layerData = layer.data as ITextLayerProps;
    const { color, fontSize, fontFamily, bold, italic, lineThrough, underline, rotate } = content;

    const { useTransparentFontColor } = options;

    const finalFontFamily = fontFamilyMap ? fontFamilyMap[fontFamily] : fontFamily;

    const styles: React.CSSProperties = {
        fontSize: `${fontSize}px`,
        fontWeight: bold ? 'bold' : 'normal',
        fontStyle: italic ? 'italic' : 'normal',
        textDecoration: `${lineThrough ? 'line-through' : ''} ${underline ? 'underline' : ''}`,
    };

    if (typeof rotate !== 'undefined') {
        // styles.display = 'inline-block';
        styles.transformOrigin = 'center';
        styles.transform = `rotate(${rotate}deg)`;
    }

    const { texture, textGradient } = layerData;
    const hasTexture = texture && texture.url;
    const hasTextGradient = textGradient && textGradient.colors.length > 1;

    if (!hasTexture && !hasTextGradient) {
        styles.color = color;
    }

    if (useTransparentFontColor) {
        styles.color = 'transparent';
    }

    if (finalFontFamily) {
        styles.fontFamily = `${finalFontFamily}`;
    }

    return styles;
};
